import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Invitation } from '@/types/schema';
import { getInvitationByToken, acceptInvitation, declineInvitation } from '@/services/invitationService';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { AuthLayout } from '@/components/auth/components/AuthLayout';

export default function InvitationAccept() {
  const { token } = useParams<{ token: string }>();
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const auth = getAuth();

  useEffect(() => {
    const fetchInvitation = async () => {
      if (!token) {
        setError('Invalid invitation link');
        setIsLoading(false);
        return;
      }

      try {
        const invitationData = await getInvitationByToken(token);
        if (invitationData) {
          // Convert Firestore timestamps to Date objects if needed
          const processedInvitation = {
            ...invitationData,
            invitedAt: invitationData.invitedAt instanceof Timestamp ? 
              invitationData.invitedAt.toDate() : 
              invitationData.invitedAt,
            expiresAt: invitationData.expiresAt instanceof Timestamp ? 
              invitationData.expiresAt.toDate() : 
              invitationData.expiresAt
          };
          
          setInvitation(processedInvitation as Invitation);
        } else {
          setError('Invitation not found or expired');
        }
      } catch (err) {
        console.error('Error fetching invitation:', err);
        setError('Failed to load invitation');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvitation();
  }, [token]);

  const handleAccept = async () => {
    if (!invitation || !token) return;
    
    setIsProcessing(true);
    try {
      const currentUser = auth.currentUser;
      const userId = currentUser ? currentUser.uid : null;
      
      if (!userId) {
        toast({
          title: 'Authentication Required',
          description: 'Please log in to accept this invitation.',
          variant: 'destructive'
        });
        navigate('/login');
        return;
      }
      
      await acceptInvitation(token, userId);
      toast({
        title: 'Invitation Accepted',
        description: 'You have successfully joined the organization.'
      });
      // Redirect to login or dashboard based on user state
      navigate('/portfolios');
    } catch (err) {
      console.error('Error accepting invitation:', err);
      toast({
        title: 'Error',
        description: 'Failed to accept invitation. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!invitation || !token) return;
    
    setIsProcessing(true);
    try {
      await declineInvitation(token);
      toast({
        title: 'Invitation Declined',
        description: 'You have declined the invitation.'
      });
      navigate('/');
    } catch (err) {
      console.error('Error declining invitation:', err);
      toast({
        title: 'Error',
        description: 'Failed to decline invitation. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <AuthLayout>
        <div className="flex items-center justify-center">
          <p>Loading invitation...</p>
        </div>
      </AuthLayout>
    );
  }

  if (error || !invitation) {
    return (
      <AuthLayout>
        <Card className="w-full">
          <CardHeader>
            <CardTitle>Invitation Error</CardTitle>
            <CardDescription>There was a problem with your invitation link.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-red-500">{error || 'Invalid invitation'}</p>
          </CardContent>
          <CardFooter>
            <Button onClick={() => navigate('/')}>Return to Home</Button>
          </CardFooter>
        </Card>
      </AuthLayout>
    );
  }

  const isExpired = new Date() > invitation.expiresAt;
  const expiryDate = format(invitation.expiresAt, 'PPP');

  return (
    <AuthLayout>
      <Card className="w-full">
        <CardHeader>
          <CardTitle>
            {isExpired ? 'Invitation Expired' : 'Join Organization'}
          </CardTitle>
          <CardDescription>
            {isExpired 
              ? `This invitation expired on ${expiryDate}.` 
              : `You've been invited to join as a ${invitation.role}.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!isExpired && (
            <>
              <p className="mb-4">
                <strong>Invited by:</strong> {invitation.invitedByName || 'An organization member'}
              </p>
              <p className="mb-4">
                <strong>Entity:</strong> {invitation.entityType === 'portfolio' ? 'Portfolio' : 'Business'}
              </p>
              <p>This invitation will expire on {expiryDate}.</p>
            </>
          )}
        </CardContent>
        <CardFooter className="flex justify-between">
          {isExpired ? (
            <Button onClick={() => navigate('/')}>Return to Home</Button>
          ) : (
            <>
              <Button 
                variant="outline" 
                onClick={handleDecline}
                disabled={isProcessing}
              >
                Decline
              </Button>
              <Button 
                onClick={handleAccept}
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Accept Invitation'}
              </Button>
            </>
          )}
        </CardFooter>
      </Card>
    </AuthLayout>
  );
}
